import { assertApiBaseUrlSameOriginContract } from './config/assertApiBaseUrlSameOriginContract.js';
import type { EnvironmentConfig } from './config/environments';
import { getViteEnvString } from './shared/vite/readViteMetaEnv.js';

/**
 * Runtime JSON may omit fields; kiosk bootstrap secret must match backend
 * `AUTH_KIOSK_SHARED_SECRET` so admin-generated kiosk links include `?secret=`.
 */
type RuntimeConfig = Partial<EnvironmentConfig> & {
  readonly kioskSharedSecret?: string;
  readonly customerAppBaseUrl?: string;
  readonly sentryDsn?: string;
  readonly sentryEnvironment?: string;
  /** Display / Sentry release label (may equal buildId on Railway SHA deploys). */
  readonly buildVersion?: string;
  /** Deploy identity for G6 version gate — prefer over buildVersion when present. */
  readonly buildId?: string;
  readonly permissions?: {
    /** Unused for UX mode selection — retained for runtime-config / env compat. */
    readonly simpleMode?: boolean | { readonly enabled?: boolean };
    readonly canonicalMetadata?: boolean | { readonly enabled?: boolean };
  };
  readonly products?: {
    readonly splitPane?: boolean | { readonly enabled?: boolean };
    readonly serverPagination?: boolean | { readonly enabled?: boolean };
  };
  readonly kiosks?: {
    /** When true (default), hide physical-kiosk sensor tiles until hardware exists. */
    readonly hideSensors?: boolean;
  };
};

const RUNTIME_CONFIG_PATH = '/runtime-config.json';

/** Empty = same-origin `/api` via Vite/Caddy (cookie auth). Absolute :3015 is cross-origin from :3001. */
const fallbackFromEnv: RuntimeConfig = {
  apiUrl: import.meta.env.VITE_API_URL ?? import.meta.env.REACT_APP_API_URL ?? '',
  wsUrl: import.meta.env.VITE_WS_URL ?? import.meta.env.REACT_APP_WS_URL ?? '',
  enableMockPayments:
    import.meta.env.REACT_APP_ENABLE_MOCK_PAYMENTS === 'true',
  paymentMode:
    (import.meta.env.REACT_APP_PAYMENT_MODE as EnvironmentConfig['paymentMode'] | undefined) ??
    'production',
  showDebugInfo:
    import.meta.env.REACT_APP_SHOW_DEBUG_INFO === 'true',
  logLevel:
    (import.meta.env.REACT_APP_LOG_LEVEL as EnvironmentConfig['logLevel'] | undefined) ??
    'warn',
  kioskSharedSecret:
    getViteEnvString('VITE_KIOSK_SHARED_SECRET') ??
    getViteEnvString('REACT_APP_KIOSK_SHARED_SECRET') ??
    getViteEnvString('AUTH_KIOSK_SHARED_SECRET'),
  customerAppBaseUrl:
    // Display / preview alignment only — must match backend AUTH_CUSTOMER_APP_BASE_URL.
    // Runtime handoff and customer auth use AUTH_* on the API, not this admin env.
    getViteEnvString('VITE_AUTH_CUSTOMER_APP_BASE_URL') ??
    getViteEnvString('REACT_APP_CUSTOMER_APP_BASE_URL') ??
    getViteEnvString('AUTH_CUSTOMER_APP_BASE_URL'),
  sentryDsn:
    getViteEnvString('VITE_SENTRY_DSN') ?? getViteEnvString('REACT_APP_SENTRY_DSN'),
  sentryEnvironment:
    getViteEnvString('VITE_SENTRY_ENVIRONMENT') ??
    getViteEnvString('REACT_APP_SENTRY_ENVIRONMENT') ??
    'development',
  buildVersion:
    getViteEnvString('VITE_BUILD_VERSION') ?? getViteEnvString('REACT_APP_BUILD_VERSION') ?? 'dev',
  buildId: getViteEnvString('VITE_BUILD_ID') ?? getViteEnvString('REACT_APP_BUILD_ID'),
  permissions: {
    simpleMode: {
      enabled: getViteEnvString('VITE_PERMISSIONS_SIMPLE_MODE_ENABLED') === 'true',
    },
    canonicalMetadata: {
      enabled: getViteEnvString('VITE_PERMISSIONS_CANONICAL_METADATA_ENABLED') !== 'false',
    },
  },
  products: {
    splitPane: {
      /** Default on — mockup docked split-pane; `false` rolls back to overlay drawer. */
      enabled: getViteEnvString('VITE_PRODUCTS_SPLIT_PANE') !== 'false',
    },
    serverPagination: {
      /** Default off — enable for AC-SCALE-01 server page/limit when catalog >500 SKUs. */
      enabled: getViteEnvString('VITE_PRODUCTS_SERVER_PAGINATION') === 'true',
    },
  },
  kiosks: {
    /** Default on (hidden) — no physical kiosk fleet yet; set `VITE_KIOSK_SENSORS_HIDE=false` to show. */
    hideSensors: getViteEnvString('VITE_KIOSK_SENSORS_HIDE') !== 'false',
  },
};

async function fetchRuntimeConfig(): Promise<RuntimeConfig | null> {
  try {
    const response = await fetch(RUNTIME_CONFIG_PATH, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as RuntimeConfig;
    return data;
  } catch {
    return null;
  }
}

export async function loadRuntimeConfig(): Promise<void> {
  const fetched = await fetchRuntimeConfig();
  const raw = fetched ?? {};
  const { kioskSharedSecret: secretFromJson, ...restFetched } = raw as RuntimeConfig & {
    readonly kioskSharedSecret?: string | null;
  };
  /** Null/empty `kioskSharedSecret` in JSON must not override Vite `.env` fallbacks. */
  const goodFromJson =
    typeof secretFromJson === 'string' && secretFromJson.trim().length > 0 ? secretFromJson.trim() : undefined;
  /** Merge so keys missing from `runtime-config.json` still come from Vite env fallbacks (e.g. kioskSharedSecret). */
  const runtimeConfig: RuntimeConfig = {
    ...fallbackFromEnv,
    ...restFetched,
    ...(goodFromJson !== undefined ? { kioskSharedSecret: goodFromJson } : {}),
  };

  const resolvedApiUrl =
    typeof runtimeConfig.apiUrl === 'string' ? runtimeConfig.apiUrl.trim() : '';
  // Assert before any credentialed session / Set-Cookie consumer path boots.
  assertApiBaseUrlSameOriginContract(resolvedApiUrl);

  if (typeof window !== 'undefined') {
    (window as typeof window & { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__ = runtimeConfig;
  }
}

export function getRuntimeConfig(): RuntimeConfig | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (window as typeof window & { __RUNTIME_CONFIG__?: RuntimeConfig }).__RUNTIME_CONFIG__;
}

